import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

function isAdminPortalError(reason) {
  return /admin portal/i.test(String(reason || ""));
}

function isAccountUnavailableError(reason) {
  return /inactive|appears to have been deleted|no longer available|school operator|غیر فعال|حذف شدہ|اسکول آپریٹر/i.test(
    String(reason || ""),
  );
}

function isAccountDeletedError(reason) {
  return /appears to have been deleted|no longer available|حذف شدہ/i.test(
    String(reason || ""),
  );
}

export default function DashboardSessionError({ reason, onRetry, retrying = false }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const adminPortal = isAdminPortalError(reason);
  const accountUnavailable = !adminPortal && isAccountUnavailableError(reason);
  const accountDeleted = accountUnavailable && isAccountDeletedError(reason);
  const nonRetryable = adminPortal || accountUnavailable;
  const isDark = theme.palette.mode === "dark";

  const title = adminPortal
    ? t("sessionError.adminPortal.title")
    : accountUnavailable
      ? accountDeleted
        ? t("sessionError.accountDeleted.title")
        : t("sessionError.accountInactive.title")
      : t("sessionError.generic.title");
  const description = adminPortal
    ? t("sessionError.adminPortal.description")
    : accountUnavailable
      ? accountDeleted
        ? t("sessionError.accountDeleted.description")
        : t("sessionError.accountInactive.description")
      : reason?.trim() || t("sessionError.generic.description");
  const icon = nonRetryable
    ? "mdi:shield-account-outline"
    : "mdi:alert-circle-outline";

  return (
    <div className="session-error-content">
      <Box
        role="alert"
        aria-live="polite"
        className="session-error-card"
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 520,
          mx: "auto",
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          background: isDark
            ? "var(--theme-ui-surface-deep)"
            : "var(--portal-theme-secondary)",
          backdropFilter: "blur(14px)",
          border: isDark
            ? "1px solid color-mix(in srgb, var(--portal-theme-color) 30%, transparent)"
            : "1px solid color-mix(in srgb, var(--portal-theme-color) 18%, transparent)",
          boxShadow: isDark
            ? "0 24px 64px var(--shadow-color)"
            : "0 20px 48px var(--shadow-color)",
        }}
      >
        <Box
          className={
            nonRetryable
              ? "session-error-card__icon session-error-card__icon--admin"
              : "session-error-card__icon session-error-card__icon--warn"
          }
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 72,
            height: 72,
            borderRadius: "50%",
            mb: 2,
          }}
        >
          <Icon icon={icon} width={40} height={40} />
        </Box>

        <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
          {title}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 400, mx: "auto", lineHeight: 1.6, mb: nonRetryable ? 2 : 3 }}
        >
          {description}
        </Typography>

        {nonRetryable ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 400, mx: "auto", lineHeight: 1.55 }}
          >
            {adminPortal
              ? t("sessionError.adminPortal.accountHint")
              : t("sessionError.accountUnavailable.accountHint")}
          </Typography>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={() => void onRetry?.()}
            disabled={retrying}
            sx={{ minWidth: 140, textTransform: "none", fontWeight: 600 }}
          >
            {retrying ? t("common.pleaseWait") : t("sessionError.retry")}
          </Button>
        )}
      </Box>
    </div>
  );
}
